// src/services/cofounder/tools/networkTools.js
import { registerTool } from './ToolRegistry';
import { db } from '../../../firebase/config';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';

export function registerNetworkTools() {
  registerTool({
    name: 'searchConnections',
    description: 'Search the user\'s network connections. Returns connected businesses.',
    category: 'read',
    requiresConfirmation: false,
    parameters: {
      type: 'object',
      properties: {
        searchTerm: {
          type: 'string',
          description: 'Optional search term to filter connections'
        }
      },
      required: []
    },
    execute: async (args, context) => {
      const businessId = context.activeBusiness?.id || context.userProfile?.businessId;
      if (!businessId) return { error: 'No business ID' };

      const connectionsRef = collection(db, 'connections');
      const q = query(
        connectionsRef,
        where('participants', 'array-contains', businessId),
        where('status', '==', 'accepted'),
        limit(50)
      );

      const snapshot = await getDocs(q);
      const connections = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      // Fetch connected business details
      const results = [];
      for (const conn of connections) {
        const otherBusinessId = conn.participants.find(p => p !== businessId);
        if (otherBusinessId) {
          try {
            const bizQ = query(
              collection(db, 'businesses'),
              where('id', '==', otherBusinessId),
              limit(1)
            );
            const bizSnap = await getDocs(bizQ);
            if (!bizSnap.empty) {
              const bizData = bizSnap.docs[0].data();
              if (!args.searchTerm ||
                  bizData.name?.toLowerCase().includes(args.searchTerm.toLowerCase()) ||
                  bizData.industry?.toLowerCase().includes(args.searchTerm.toLowerCase())) {
                results.push({
                  businessId: otherBusinessId,
                  name: bizData.name,
                  industry: bizData.industry,
                  category: bizData.category
                });
              }
            }
          } catch (e) {
            // Skip failed lookups
          }
        }
      }

      return { connections: results, total: results.length };
    }
  });

  registerTool({
    name: 'findSuppliers',
    description: 'Search the network for potential suppliers in a specific industry or category.',
    category: 'read',
    requiresConfirmation: false,
    parameters: {
      type: 'object',
      properties: {
        industry: {
          type: 'string',
          description: 'Industry to search for suppliers in'
        },
        category: {
          type: 'string',
          description: 'Specific business category'
        }
      },
      required: []
    },
    execute: async (args, context) => {
      const constraints = [limit(20)];
      if (args.industry) {
        constraints.unshift(where('industry', '==', args.industry));
      }
      if (args.category) {
        constraints.unshift(where('category', '==', args.category));
      }

      const q = query(collection(db, 'businesses'), ...constraints);
      const snapshot = await getDocs(q);

      const businesses = snapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(b => b.id !== (context.activeBusiness?.id || context.userProfile?.businessId))
        .map(b => ({
          name: b.name,
          industry: b.industry,
          category: b.category,
          location: b.address || b.location
        }));

      return { suppliers: businesses, total: businesses.length };
    }
  });

  registerTool({
    name: 'findPotentialCustomers',
    description: 'Search the network for potential customers or business partners.',
    category: 'read',
    requiresConfirmation: false,
    parameters: {
      type: 'object',
      properties: {
        industry: {
          type: 'string',
          description: 'Target industry'
        }
      },
      required: []
    },
    execute: async (args, context) => {
      const constraints = [limit(20)];
      if (args.industry) {
        constraints.unshift(where('industry', '==', args.industry));
      }

      const q = query(collection(db, 'businesses'), ...constraints);
      const snapshot = await getDocs(q);

      const businesses = snapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(b => b.id !== (context.activeBusiness?.id || context.userProfile?.businessId))
        .map(b => ({
          name: b.name,
          industry: b.industry,
          category: b.category,
          location: b.address || b.location
        }));

      return { potentialCustomers: businesses, total: businesses.length };
    }
  });

  registerTool({
    name: 'searchNetworkPosts',
    description: 'Search network posts for topics, trends, or specific content.',
    category: 'read',
    requiresConfirmation: false,
    parameters: {
      type: 'object',
      properties: {
        searchTerm: {
          type: 'string',
          description: 'Search term for posts'
        }
      },
      required: []
    },
    execute: async (args, context) => {
      const q = query(
        collection(db, 'posts'),
        orderBy('createdAt', 'desc'),
        limit(20)
      );
      const snapshot = await getDocs(q);

      let posts = snapshot.docs.map(d => ({
        id: d.id,
        content: d.data().content?.substring(0, 200),
        tags: d.data().tags,
        likes: d.data().likeCount || 0,
        createdAt: d.data().createdAt?.toDate?.()?.toISOString()
      }));

      if (args.searchTerm) {
        const term = args.searchTerm.toLowerCase();
        posts = posts.filter(p =>
          p.content?.toLowerCase().includes(term) ||
          p.tags?.some(t => t.toLowerCase().includes(term))
        );
      }

      return { posts, total: posts.length };
    }
  });
}
